/* eslint-disable @atlassian/perf-linting/no-chain-state-updates */
import { useState, useEffect } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { cfetch, BadStatusError } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type {
	useIsInconsistentLicenseQuery$data,
	useIsInconsistentLicenseQuery$variables,
} from './__generated__/useIsInconsistentLicenseQuery.graphql.ts';

const BULK_PERMITTED_API = '/gateway/api/permissions/bulk/permitted';

const LICENSED_STATUSES: Set<AccessStatus> = new Set([
	AccessStatus.EXTERNAL_COLLABORATOR_ACCESS,
	AccessStatus.LICENSED_ADMIN_ACCESS,
	AccessStatus.LICENSED_USE_ACCESS,
]);

const isLicensedStatus = (accessStatus: AccessStatus | null) =>
	!!accessStatus && LICENSED_STATUSES.has(accessStatus);

const CONFLUENCE_ACCESS_STATUS_QUERY = graphql`
	query useIsInconsistentLicenseQuery {
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				accessStatus
			}
		}
	}
`;

const makeSiteAri = (cloudId: string) => `ari:cloud:confluence::site/${cloudId}`;
const makePermsRequestBody = (cloudId: string) =>
	JSON.stringify([
		{
			permissionId: 'write',
			resourceId: makeSiteAri(cloudId),
		},
		{
			permissionId: 'external-collaborator-write',
			resourceId: makeSiteAri(cloudId),
		},
	]);

interface PermittedResponse {
	principalId: string;
	resourceId: string;
	permissionId: string;
	permitted: boolean;
	dontRequirePrincipalInSite: boolean;
}

export const useIsInconsistentLicense = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isLoggedIn, accessStatus: sessionDataAccessStatus, cloudId } = useSessionData();
	const [allowPolling, setAllowPolling] = useState<boolean>(false);

	const { data, error, startPolling, stopPolling } = useQueryInterop<
		useIsInconsistentLicenseQuery$data,
		useIsInconsistentLicenseQuery$variables
	>(CONFLUENCE_ACCESS_STATUS_QUERY, {
		fetchPolicy: 'network-only',
		notifyOnNetworkStatusChange: true,
		skip: !isLoggedIn || isLicensedStatus(sessionDataAccessStatus) || !allowPolling,
	});

	if (error) {
		markErrorAsHandled(error);
	}

	const latestAccessStatus: AccessStatus | null =
		(data?.user?.confluence?.accessStatus as AccessStatus | undefined) ?? sessionDataAccessStatus;

	const isLicensedInConfluence = isLicensedStatus(latestAccessStatus);

	const [isLicensedInIdentity, setIsLicensedInIdentity] = useState<boolean>(false);

	const isInconsistentLicense = !isLicensedInConfluence && isLicensedInIdentity;

	/** Determine user's license status in Identity */
	useEffect(() => {
		/** No point to run any of this if user is already licensed in Confluence */
		if (!isLoggedIn || !cloudId || isLicensedInConfluence) {
			return;
		}

		const callIdentityPerms = async () => {
			try {
				const response = await cfetch(BULK_PERMITTED_API, {
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: makePermsRequestBody(cloudId),
				});

				if (response.ok) {
					const result: PermittedResponse[] = await response.json();
					const isLicensedInIdentity = result.some((entry) => entry.permitted);
					setIsLicensedInIdentity(isLicensedInIdentity);
				} else {
					throw new BadStatusError(`${response.status}: ${response.statusText}`, response);
				}
			} catch (e) {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: 'inconsistentLicenseCheck',
						action: 'failed',
						actionSubject: 'callIdentityPerms',
						attributes: {
							status: BadStatusError.isBadStatusError(e) ? e.response.status : 'unknown',
							statusText: BadStatusError.isBadStatusError(e) ? e.response.statusText : 'unknown',
							message: e instanceof Error ? e.message : 'unknown',
						},
					},
				}).fire();
			}
		};

		void callIdentityPerms();
	}, [isLoggedIn, cloudId, isLicensedInConfluence, createAnalyticsEvent]);

	/** Only poll when there are discrepancies in the license */
	useEffect(() => {
		if (isInconsistentLicense) {
			setAllowPolling(true);
			startPolling(10000);
		} else {
			stopPolling();
		}
	}, [isInconsistentLicense, startPolling, stopPolling]);

	/** Definitely stop polling on unmount */
	useEffect(() => {
		return () => stopPolling();
	}, [stopPolling]);

	useEffect(() => {
		if (isInconsistentLicense) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'foundInconsistency',
					actionSubject: 'inconsistentLicenseCheck',
					source: 'inconsistentLicenseCheck',
					attributes: {
						sessionDataAccessStatus,
						latestAccessStatus,
						isLicensedInIdentity,
						cloudId,
					},
				},
			}).fire();
		}
	}, [
		isInconsistentLicense,
		isLicensedInIdentity,
		createAnalyticsEvent,
		sessionDataAccessStatus,
		latestAccessStatus,
		cloudId,
	]);

	return {
		isInconsistentLicense,
	};
};
